import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  Package,
  AlertTriangle,
  XCircle,
  Edit3,
  History,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Textarea from '@/components/ui/Textarea';
import Modal from '@/components/ui/Modal';
import Pagination from '@/components/ui/Pagination';
import Skeleton from '@/components/ui/Skeleton';
import api from '@/lib/api';
import { formatDate, cn } from '@/lib/utils';
import { useUIStore } from '@/stores/ui-store';

interface InventoryItem {
  product_id: string;
  variant_id: string;
  product_name: string;
  variant_name: string | null;
  variant_sku: string;
  price: number;
  primary_image_url: string | null;
  quantity: number;
  reserved_quantity: number;
  available_quantity: number;
  low_stock_threshold: number;
  stock_status: 'in_stock' | 'low_stock' | 'out_of_stock';
  /** False when this variant is sold without counting its stock. */
  track_quantity: boolean;
}

interface InventoryLog {
  id: string;
  product_name: string;
  product_id: string;
  variant_name: string | null;
  variant_sku: string;
  change_quantity: number;
  previous_quantity: number;
  new_quantity: number;
  reason: string;
  notes: string | null;
  created_by_name: string | null;
  created_by_last_name: string | null;
  created_at: string;
}

interface PaginationData {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

interface InventoryResponse {
  items: InventoryItem[];
  pagination: PaginationData;
}

interface InventoryLogResponse {
  logs: InventoryLog[];
  pagination: PaginationData;
}

interface AdjustmentFormData {
  adjustment_type: 'increase' | 'decrease';
  quantity: string;
  reason: string;
  notes: string;
}

type FilterType = 'all' | 'low_stock' | 'out_of_stock';

const getStatusBadge = (status: InventoryItem['stock_status']) => {
  const variants = {
    in_stock: 'green' as const,
    low_stock: 'yellow' as const,
    out_of_stock: 'red' as const
  };
  const labels = {
    in_stock: 'In Stock',
    low_stock: 'Low Stock',
    out_of_stock: 'Out of Stock'
  };
  return { variant: variants[status], label: labels[status] };
};

const getReasonLabel = (reason: string) => {
  const labels: Record<string, string> = {
    manual_adjustment: 'Manual Adjustment',
    order_placed: 'Order Placed',
    order_cancelled: 'Order Cancelled',
    restock: 'Restock',
    return: 'Customer Return',
    damage: 'Damaged/Lost',
  };
  return labels[reason] || reason;
};

const Inventory = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const addToast = useUIStore((s) => s.addToast);

  const [showLogs, setShowLogs] = useState(false);
  const [adjustmentModal, setAdjustmentModal] = useState<{
    isOpen: boolean;
    item: InventoryItem | null;
  }>({ isOpen: false, item: null });
  const [adjustmentForm, setAdjustmentForm] = useState<AdjustmentFormData>({
    adjustment_type: 'increase',
    quantity: '',
    reason: '',
    notes: ''
  });
  const [adjustmentError, setAdjustmentError] = useState<string | null>(null);

  // URL-driven state
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const searchQuery = searchParams.get('q') || '';
  const filter = (searchParams.get('filter') || 'all') as FilterType;

  // Fetch inventory
  const { data: inventoryData, isLoading, error } = useQuery({
    queryKey: ['admin-inventory', currentPage, searchQuery, filter],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', currentPage.toString());
      params.set('limit', '20');
      if (searchQuery) params.set('q', searchQuery);
      if (filter === 'low_stock') params.set('low_stock', 'true');
      if (filter === 'out_of_stock') params.set('out_of_stock', 'true');
      return api.get<InventoryResponse>(`/admin/inventory?${params.toString()}`);
    },
  });

  const items = inventoryData?.items ?? [];
  const pagination = inventoryData?.pagination ?? { page: 1, limit: 20, total: 0, total_pages: 0 };

  // Fetch logs
  const { data: logData, isLoading: isLogLoading } = useQuery({
    queryKey: ['admin-inventory-log'],
    queryFn: () => api.get<InventoryLogResponse>('/admin/inventory/log?limit=50'),
    enabled: showLogs,
  });

  const logs = logData?.logs ?? [];

  // Adjustment mutation
  const adjustMutation = useMutation({
    mutationFn: async ({ variantId, change_quantity, reason, notes }: {
      variantId: string;
      change_quantity: number;
      reason: string;
      notes: string | null;
    }) => {
      return api.put(`/admin/inventory/${variantId}/adjust`, { change_quantity, reason, notes });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-inventory'] });
      queryClient.invalidateQueries({ queryKey: ['admin-inventory-log'] });
      addToast('Stock adjusted successfully', 'success');
      closeAdjustmentModal();
    },
    onError: (err: Error) => {
      setAdjustmentError(err.message || 'Failed to adjust inventory');
    },
  });

  // Count a variant's stock, or sell it without counting.
  const trackingMutation = useMutation({
    mutationFn: async ({ variantId, track_quantity }: { variantId: string; track_quantity: boolean }) => {
      return api.put(`/admin/inventory/${variantId}/tracking`, { track_quantity });
    },
    onSuccess: (_data, { track_quantity }) => {
      queryClient.invalidateQueries({ queryKey: ['admin-inventory'] });
      addToast(track_quantity ? 'Stock is counted for this item again' : 'This item is now sold without counting stock', 'success');
    },
    onError: (err: Error) => {
      addToast(err.message || 'Failed to update stock counting', 'error');
    },
  });

  const updateSearchParams = (updates: Record<string, string>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    if ('q' in updates || 'filter' in updates) {
      newParams.set('page', '1');
    }
    setSearchParams(newParams);
  };

  const handleSearch = (value: string) => {
    updateSearchParams({ q: value });
  };

  const handleFilter = (filterType: FilterType) => {
    updateSearchParams({ filter: filterType === 'all' ? '' : filterType });
  };

  const handlePageChange = (page: number) => {
    updateSearchParams({ page: page.toString() });
  };

  const openAdjustmentModal = (item: InventoryItem) => {
    setAdjustmentModal({ isOpen: true, item });
    setAdjustmentForm({ adjustment_type: 'increase', quantity: '', reason: '', notes: '' });
    setAdjustmentError(null);
  };

  const closeAdjustmentModal = () => {
    setAdjustmentModal({ isOpen: false, item: null });
    setAdjustmentForm({ adjustment_type: 'increase', quantity: '', reason: '', notes: '' });
    setAdjustmentError(null);
  };

  const handleAdjustmentSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!adjustmentModal.item || !adjustmentForm.quantity || !adjustmentForm.reason) return;

    const qty = parseInt(adjustmentForm.quantity, 10);
    const change_quantity = adjustmentForm.adjustment_type === 'increase' ? qty : -qty;

    adjustMutation.mutate({
      variantId: adjustmentModal.item.variant_id,
      change_quantity,
      reason: adjustmentForm.reason,
      notes: adjustmentForm.notes || null,
    });
  };

  if (error && !adjustmentModal.isOpen) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          <p className="font-medium">Error loading inventory</p>
          <p className="text-sm mt-1">{error instanceof Error ? error.message : 'Failed to load inventory'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Inventory</h1>
          <p className="text-gray-600 mt-1">Manage stock levels and track inventory changes</p>
        </div>
        <Button
          variant={showLogs ? 'primary' : 'outline'}
          size="md"
          onClick={() => setShowLogs(!showLogs)}
        >
          <History className="w-5 h-5 mr-2" aria-hidden="true" />
          {showLogs ? 'Hide' : 'Show'} Activity Log
        </Button>
      </div>

      {/* Search and Filters */}
      <Card padding="md">
        <div className="space-y-4">
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
              aria-hidden="true"
            />
            <input
              type="search"
              placeholder="Search by product name, variant, or SKU..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              aria-label="Search inventory"
            />
          </div>

          <div className="flex gap-2 border-b border-gray-200">
            {(['all', 'low_stock', 'out_of_stock'] as FilterType[]).map((filterType) => (
              <button
                key={filterType}
                onClick={() => handleFilter(filterType)}
                className={cn(
                  'px-4 py-2 font-medium text-sm border-b-2 transition-colors flex items-center gap-2',
                  filter === filterType
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                )}
                aria-current={filter === filterType ? 'page' : undefined}
              >
                {filterType === 'all' && <Package className="w-4 h-4" aria-hidden="true" />}
                {filterType === 'low_stock' && <AlertTriangle className="w-4 h-4" aria-hidden="true" />}
                {filterType === 'out_of_stock' && <XCircle className="w-4 h-4" aria-hidden="true" />}
                {filterType === 'all' && 'All Items'}
                {filterType === 'low_stock' && 'Low Stock'}
                {filterType === 'out_of_stock' && 'Out of Stock'}
              </button>
            ))}
          </div>
        </div>
      </Card>

      {/* Inventory Table */}
      {!showLogs && (
        <Card padding="none">
          {isLoading ? (
            <div className="p-6">
              <Skeleton className="h-96" />
            </div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center">
              <Package className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No inventory items found</h3>
              <p className="text-gray-600">
                {searchQuery || filter !== 'all'
                  ? 'Try adjusting your search or filters'
                  : 'Inventory items will appear here once you add products with variants'}
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Product
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        SKU
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Current
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Reserved
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Available
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Threshold
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {items.map((item) => {
                      const statusBadge = getStatusBadge(item.stock_status);
                      return (
                        <tr key={item.variant_id} className="hover:bg-gray-50">
                          <td className="px-4 py-4">
                            <Link to={`/admin/products/${item.product_id}`} className="block">
                              {item.primary_image_url ? (
                                <img src={item.primary_image_url} alt="" className="w-10 h-10 object-cover rounded-lg" />
                              ) : (
                                <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                                  <Package className="w-5 h-5 text-gray-400" />
                                </div>
                              )}
                            </Link>
                          </td>
                          <td className="px-4 py-4">
                            <Link to={`/admin/products/${item.product_id}`} className="block group">
                              <div className="font-medium text-gray-900 group-hover:text-blue-600">{item.product_name}</div>
                              {item.variant_name && (
                                <div className="text-sm text-gray-500">{item.variant_name}</div>
                              )}
                            </Link>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                            {item.variant_sku}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-right">
                            <span className="text-sm font-semibold text-gray-900">
                              {item.quantity}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-right text-sm text-gray-600">
                            {item.reserved_quantity}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-right">
                            {item.track_quantity ? (
                              <span className="text-sm font-medium text-gray-900">
                                {item.available_quantity}
                              </span>
                            ) : (
                              <span className="text-sm text-gray-500" title="Sold without counting stock: always available">
                                Not counted
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-right text-sm text-gray-500">
                            {item.low_stock_threshold}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <Badge variant={statusBadge.variant} className="min-w-[5rem] justify-center">{statusBadge.label}</Badge>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-right">
                            <button
                              onClick={() => openAdjustmentModal(item)}
                              className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1 text-sm font-medium"
                              aria-label={`Adjust stock for ${item.product_name}`}
                            >
                              <Edit3 className="w-4 h-4" aria-hidden="true" />
                              Adjust
                            </button>
                            <button
                              onClick={() => trackingMutation.mutate({ variantId: item.variant_id, track_quantity: !item.track_quantity })}
                              disabled={trackingMutation.isPending}
                              className="ml-3 text-gray-600 hover:text-gray-900 text-sm font-medium disabled:opacity-50"
                              aria-label={`${item.track_quantity ? 'Stop counting' : 'Count'} stock for ${item.product_name}`}
                            >
                              {item.track_quantity ? 'Stop counting' : 'Count stock'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {pagination.total_pages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Showing{' '}
                    <span className="font-medium">
                      {(pagination.page - 1) * pagination.limit + 1}
                    </span>{' '}
                    to{' '}
                    <span className="font-medium">
                      {Math.min(pagination.page * pagination.limit, pagination.total)}
                    </span>{' '}
                    of <span className="font-medium">{pagination.total}</span> items
                  </div>
                  <Pagination
                    currentPage={pagination.page}
                    totalPages={pagination.total_pages}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}
            </>
          )}
        </Card>
      )}

      {/* Activity Log */}
      {showLogs && (
        <Card title="Inventory Activity Log" padding="none">
          {isLogLoading ? (
            <div className="p-6">
              <Skeleton className="h-96" />
            </div>
          ) : logs.length === 0 ? (
            <div className="p-12 text-center">
              <History className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No activity yet</h3>
              <p className="text-gray-600">Inventory changes will be logged here</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date & Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Product
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      SKU
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Change
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Prev Qty
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      New Qty
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Reason
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      By
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {logs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatDate(log.created_at)}
                      </td>
                      <td className="px-6 py-4">
                        <Link to={`/admin/products/${log.product_id}`} className="block group">
                          <div className="text-sm font-medium text-gray-900 group-hover:text-blue-600">
                            {log.product_name}
                          </div>
                          {log.variant_name && (
                            <div className="text-xs text-gray-500">{log.variant_name}</div>
                          )}
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {log.variant_sku}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div
                          className={cn(
                            'inline-flex items-center gap-1 text-sm font-semibold',
                            log.change_quantity > 0 ? 'text-green-600' : 'text-red-600'
                          )}
                        >
                          {log.change_quantity > 0 ? (
                            <TrendingUp className="w-4 h-4" aria-hidden="true" />
                          ) : (
                            <TrendingDown className="w-4 h-4" aria-hidden="true" />
                          )}
                          {log.change_quantity > 0 ? '+' : ''}
                          {log.change_quantity}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500">
                        {log.previous_quantity}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-gray-900">
                        {log.new_quantity}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{getReasonLabel(log.reason)}</div>
                        {log.notes && (
                          <div className="text-xs text-gray-500 mt-1">{log.notes}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {log.created_by_name
                          ? `${log.created_by_name} ${log.created_by_last_name || ''}`.trim()
                          : 'System'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Stock Adjustment Modal */}
      <Modal
        isOpen={adjustmentModal.isOpen}
        onClose={closeAdjustmentModal}
        title="Adjust Stock Level"
      >
        {adjustmentModal.item && (
          <form onSubmit={handleAdjustmentSubmit} className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="font-medium text-gray-900">
                {adjustmentModal.item.product_name}
              </div>
              {adjustmentModal.item.variant_name && (
                <div className="text-sm text-gray-600">{adjustmentModal.item.variant_name}</div>
              )}
              <div className="text-sm text-gray-500 mt-1">
                SKU: {adjustmentModal.item.variant_sku}
              </div>
              <div className="text-sm text-gray-900 mt-2">
                Current Stock:{' '}
                <span className="font-semibold">{adjustmentModal.item.quantity}</span>
              </div>
            </div>

            {adjustmentError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
                {adjustmentError}
              </div>
            )}

            <Select
              label="Adjustment Type"
              value={adjustmentForm.adjustment_type}
              onChange={(e) =>
                setAdjustmentForm((prev) => ({
                  ...prev,
                  adjustment_type: e.target.value as 'increase' | 'decrease'
                }))
              }
              options={[
                { label: 'Increase Stock', value: 'increase' },
                { label: 'Decrease Stock', value: 'decrease' }
              ]}
            />

            <Input
              label="Quantity"
              type="number"
              min="1"
              value={adjustmentForm.quantity}
              onChange={(e) =>
                setAdjustmentForm((prev) => ({ ...prev, quantity: e.target.value }))
              }
              placeholder="Enter quantity to adjust"
              required
            />

            <Select
              label="Reason"
              value={adjustmentForm.reason}
              onChange={(e) =>
                setAdjustmentForm((prev) => ({ ...prev, reason: e.target.value }))
              }
              options={[
                { label: 'Select reason', value: '' },
                { label: 'Manual Adjustment', value: 'manual_adjustment' },
                { label: 'Restock', value: 'restock' },
                { label: 'Customer Return', value: 'return' },
                { label: 'Damaged/Lost', value: 'damage' }
              ]}
              required
            />

            <Textarea
              label="Notes (Optional)"
              value={adjustmentForm.notes}
              onChange={(e) =>
                setAdjustmentForm((prev) => ({ ...prev, notes: e.target.value }))
              }
              placeholder="Add any additional notes about this adjustment..."
              rows={3}
            />

            {adjustmentForm.quantity && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  New stock level will be:{' '}
                  <span className="font-semibold">
                    {Math.max(0, adjustmentForm.adjustment_type === 'increase'
                      ? adjustmentModal.item.quantity + parseInt(adjustmentForm.quantity, 10)
                      : adjustmentModal.item.quantity - parseInt(adjustmentForm.quantity, 10))}
                  </span>
                </p>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" size="md" type="button" onClick={closeAdjustmentModal}>
                Cancel
              </Button>
              <Button variant="primary" size="md" type="submit" isLoading={adjustMutation.isPending}>
                Confirm Adjustment
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default Inventory;
